"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const lodash_uniqueid_1 = __importDefault(require("lodash.uniqueid"));
const uswds_react_utils_1 = require("@boozallen-uip/uswds-react-utils");
const HeaderRow_1 = __importDefault(require("./HeaderRow"));
const TableBody_1 = __importDefault(require("./TableBody"));
const RenderQueue_1 = __importDefault(require("../../managers/RenderQueue"));
const ScrollManager_1 = __importDefault(require("../../managers/ScrollManager"));
const scrollbarHeight = 10;
class Table extends react_1.default.Component {
    constructor(props) {
        super(props);
        this.restorePointerTimer = null;
        this.headerComponent = null;
        this.bodyComponent = null;
        this.tableWrapper = null;
        this.internalDiv = null;
        this.tableId = `${(0, lodash_uniqueid_1.default)()}`;
        this.scrolledTable = this.scrolledTable.bind(this);
        this.scrolledHeader = this.scrolledHeader.bind(this);
    }
    reloadTable() {
        if (this.tableWrapper) {
            // force the scroll back to the top of the page
            this.tableWrapper.scrollLeft = 0;
            this.tableWrapper.scrollTop = 0;
        }
        // wait for the scroll to complete before changing the row counts (to prevent false
        // pagination)
        /* istanbul ignore next */
        window.setTimeout(() => {
            if (this.bodyComponent) {
                this.bodyComponent.reloadTable();
            }
        }, 300);
    }
    scrollTo(x, y) {
        const scrollOperation = {
            operation: () => {
                // this check is here to make sure the table and header wrappers exist
                // this condition is hard to re-create in Jest, so we ignore it for coverage
                /* istanbul ignore if */
                if (!this.tableWrapper || !this.headerWrapper) {
                    return;
                }
                this.tableWrapper.scrollLeft = x;
                this.tableWrapper.scrollTop = y;
                this.headerWrapper.scrollLeft = 0;
            },
            type: 'overrideScroll'
        };
        RenderQueue_1.default.addWrite(scrollOperation);
    }
    scrolledHeader() {
        // The header can only scroll through the use of accessibility hooks.
        // Because there are two scrolling elements (the transform offset + the scroll offset), we
        // need to combine them into a single scroll offset.
        const scrollOperation = {
            operation: () => {
                const headerX = this.headerComponent.currentX || 0;
                const realX = this.headerWrapper.scrollLeft + headerX;
                const x = realX;
                const y = this.tableWrapper.scrollTop;
                ScrollManager_1.default.update({
                    x,
                    y
                });
                // Scroll events in the table body will be applied as transforms while native
                // header scrolls via accessibility hooks, so we need to reset the header scrollLeft
                // to 0 and apply the full horizontal offset via transforms.
                // By calling the scrollTo function, we will apply the full horizontal offset to the
                // table body while simultaneously resetting the header scrollLeft to 0. The table
                // body's scrollLeft change will trigger the same scroll event that occurs from
                // native mouse scrolls in the table body (scrolledTable). scrolledTable will
                // synchronize the horizontal offset in the header entirely via transforms.
                this.scrollTo(x, y);
            },
            type: 'scroll'
        };
        RenderQueue_1.default.addRead(scrollOperation);
    }
    scrolledTable() {
        const scrollOperation = {
            operation: () => {
                const x = this.tableWrapper.scrollLeft;
                const y = this.tableWrapper.scrollTop;
                ScrollManager_1.default.update({
                    x,
                    y
                });
            },
            type: 'scroll'
        };
        const pointerOperation = {
            operation: () => {
                if (this.restorePointerTimer) {
                    window.clearTimeout(this.restorePointer);
                    this.restorePointerTimer = null;
                }
                this.internalDiv.style.pointerEvents = 'none';
                this.restorePointerTimer = window.setTimeout(() => {
                    this.internalDiv.style.pointerEvents = 'auto';
                }, 150);
            },
            type: 'pointer'
        };
        RenderQueue_1.default.addRead(scrollOperation);
        RenderQueue_1.default.addWrite(pointerOperation);
    }
    render() {
        const { bodyHeight, bodyWidth, columns, contentWidth, headerHeight, rowCount, rowHeight } = this.props;
        const tableHeight = bodyHeight
            ? bodyHeight
            : // setting the table height to a partial row clearly indicates when there's more data
                29.5 * rowHeight;
        const totalHeight = rowCount * rowHeight;
        const needsVerticalScroll = totalHeight > tableHeight;
        const visibleWidth = bodyWidth ? Math.min(bodyWidth, contentWidth) : '100%';
        const visibleHeight = Math.min(tableHeight, totalHeight);
        const headerStyle = {
            minWidth: visibleWidth,
            maxWidth: visibleWidth,
            maxHeight: headerHeight,
            minHeight: headerHeight
        };
        const style = {
            minWidth: visibleWidth,
            maxWidth: visibleWidth,
            minHeight: visibleHeight + headerHeight
        };
        const bodyStyle = {
            minWidth: visibleWidth,
            maxWidth: visibleWidth,
            height: needsVerticalScroll
                ? visibleHeight
                : visibleHeight + scrollbarHeight
        };
        if (needsVerticalScroll) {
            style.maxHeight = visibleHeight + headerHeight;
            bodyStyle.maxHeight = visibleHeight;
        }
        const contentStyle = {
            width: contentWidth,
            height: totalHeight
        };
        let accessibleDescription = `${columns.visibleOrder.length} column`;
        if (columns.visibleOrder.length !== 1) {
            accessibleDescription += 's';
        }
        accessibleDescription += ` and ${rowCount} row`;
        if (rowCount !== 1) {
            accessibleDescription += 's';
        }
        return ((0, jsx_runtime_1.jsxs)("div", { className: "bah-uswds-table-container", role: "grid", "aria-rowcount": -1, "aria-colcount": this.props.columns.visibleOrder.length, "aria-label": `This is a table with ${accessibleDescription}. Use your arrow keys to navigate through cells.`, style: style, children: [(0, jsx_runtime_1.jsx)("div", { className: "bah-uswds-table-header-container", role: "presentation", style: headerStyle, onScroll: this.scrolledHeader, ref: (div) => {
                        this.headerWrapper = div;
                    }, children: (0, jsx_runtime_1.jsx)(HeaderRow_1.default, { borderless: this.props.borderless, contentWidth: this.props.contentWidth, columns: this.props.columns, columnCoords: this.props.columnCoords, currentSort: this.props.currentSort, headerHeight: this.props.headerHeight, sortFn: this.props.sortFn, tableId: this.tableId, ref: (component) => {
                            this.headerComponent = component;
                        } }) }), this.props.rowCount > 0 ? ((0, jsx_runtime_1.jsx)("div", { className: (0, uswds_react_utils_1.createClassString)('bah-uswds-table-body-section', needsVerticalScroll && 'bah-uswds-table-body-section__scrollable'), role: "presentation", style: bodyStyle, onScroll: this.scrolledTable, ref: (div) => {
                        this.tableWrapper = div;
                    }, children: (0, jsx_runtime_1.jsx)("div", { className: "bah-uswds-table-content", role: "presentation", style: contentStyle, ref: (div) => {
                            this.internalDiv = div;
                        }, children: (0, jsx_runtime_1.jsx)(TableBody_1.default, { borderless: this.props.borderless, tableId: this.tableId, data: this.props.data, columns: this.props.columns, columnCoords: this.props.columnCoords, contentWidth: this.props.contentWidth, bodyWidth: visibleWidth, bodyHeight: visibleHeight, rowHeight: this.props.rowHeight, rowCount: this.props.rowCount, onReachedBottom: this.props.onReachedBottom, ref: (component) => {
                                this.bodyComponent = component;
                            } }) }) })) : ((0, jsx_runtime_1.jsx)("div", { className: "bah-uswds-table-no-data", style: { width: visibleWidth }, children: (0, jsx_runtime_1.jsx)("div", { children: "No data to display." }) }))] }));
    }
}
exports.default = Table;
